import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Save, Search, Plus } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Flag } from "@/components/Flag";

const LANGS = ["fr", "en", "es", "pt"];

export const TranslationsPanel = ({ isSuper }) => {
  const { lang: uiLang, setConfig } = useApp();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState({});
  const [orig, setOrig] = useState({});
  // Lien profond : /admin?tab=i18n&q=<clé> (ex. depuis Supervision › FAQ — retours) pré-remplit la recherche.
  const [q, setQ] = useState(() => searchParams.get("q") || "");
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState("");

  useEffect(() => {
    api.get("/admin/i18n").then(({ data }) => {
      const map = {};
      (data.translations || []).forEach((tr) => {
        if (!map[tr.key]) map[tr.key] = {};
        map[tr.key][tr.lang] = tr.value;
      });
      setRows(map);
      setOrig(JSON.parse(JSON.stringify(map)));
    }).catch(() => {});
  }, []);

  const setVal = (key, l, v) => setRows((r) => ({ ...r, [key]: { ...r[key], [l]: v } }));

  const dirtyCount = useMemo(() => {
    let n = 0;
    Object.keys(rows).forEach((k) => LANGS.forEach((l) => {
      if ((rows[k]?.[l] ?? "") !== (orig[k]?.[l] ?? "")) n += 1;
    }));
    return n;
  }, [rows, orig]);

  const save = async () => {
    const perLang = {};
    Object.keys(rows).forEach((k) => LANGS.forEach((l) => {
      const cur = rows[k]?.[l] ?? "";
      const old = orig[k]?.[l] ?? "";
      if (cur !== old && cur !== "") {
        perLang[l] = perLang[l] || {};
        perLang[l][k] = cur;
      }
    }));
    const langs = Object.keys(perLang);
    if (!langs.length) return toast.info("Aucun changement");
    setSaving(true);
    try {
      for (const l of langs) await api.patch("/admin/i18n", { lang: l, entries: perLang[l] });
      toast.success("Traductions mises à jour");
      setOrig(JSON.parse(JSON.stringify(rows)));
      try { const c = await api.get(`/config/public?lang=${uiLang}`); setConfig(c.data); } catch { /* noop */ }
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setSaving(false); }
  };

  const addKey = () => {
    const k = newKey.trim().toLowerCase().replace(/\s+/g, "_");
    if (!k) return;
    if (rows[k]) return toast.error("Cette clé existe déjà");
    setRows((r) => ({ ...r, [k]: { fr: "", en: "", es: "", pt: "" } }));
    setNewKey("");
    setQ(k);
    toast.success(`Clé « ${k} » ajoutée — remplis les traductions puis sauvegarde`);
  };

  const keys = useMemo(() => {
    const all = Object.keys(rows).sort();
    if (!q.trim()) return all;
    const s = q.toLowerCase();
    return all.filter((k) => k.toLowerCase().includes(s) || LANGS.some((l) => (rows[k]?.[l] || "").toLowerCase().includes(s)));
  }, [rows, q]);

  const sections = useMemo(() => {
    const out = [];
    let last = null;
    keys.forEach((k) => {
      const prefix = k.includes(".") ? k.split(".")[0] : "autres";
      if (prefix !== last) { out.push({ type: "header", prefix }); last = prefix; }
      out.push({ type: "row", key: k });
    });
    return out;
  }, [keys]);

  return (
    <div className="space-y-3" data-testid="admin-i18n-panel">
      <div className="yalola-card p-4 space-y-3">
        <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
          Modifie n'importe quel mot, phrase ou bouton de l'application, dans les 4 langues côte à côte. Les changements s'appliquent immédiatement après sauvegarde.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
            <Input className="pl-9" placeholder="Rechercher une clé ou un texte..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="i18n-search" />
          </div>
          {isSuper && (
            <Button className="pill-primary rounded-full" onClick={save} disabled={saving || dirtyCount === 0} data-testid="i18n-save-btn">
              <Save size={14} className="mr-1" /> Sauvegarder{dirtyCount > 0 ? ` (${dirtyCount})` : ""}
            </Button>
          )}
        </div>
        {isSuper && (
          <div className="flex items-center gap-2">
            <Input placeholder="Nouvelle clé (ex: nav.boutique)" value={newKey} onChange={(e) => setNewKey(e.target.value)} className="max-w-[280px]" data-testid="i18n-new-key" />
            <Button variant="outline" className="rounded-full" onClick={addKey} data-testid="i18n-add-key-btn"><Plus size={14} className="mr-1" /> Ajouter une clé</Button>
          </div>
        )}
      </div>

      <div className="yalola-card p-4 max-h-[65vh] overflow-y-auto overflow-x-auto">
        <div className="min-w-[820px]">
          <div className="grid grid-cols-[190px_repeat(4,1fr)] gap-2 items-center pb-2 border-b sticky top-0 z-10" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface, #fff)" }}>
            <div className="text-xs font-semibold uppercase" style={{ color: "var(--yalola-text-muted)" }}>Clé</div>
            {LANGS.map((l) => (
              <div key={l} className="text-xs font-semibold uppercase flex items-center gap-1.5" style={{ color: "var(--yalola-text-muted)" }}>
                <Flag code={l} size={14} /> {l}
              </div>
            ))}
          </div>
          <div className="space-y-1.5 mt-2">
            {sections.map((item) => item.type === "header" ? (
              <div key={`h-${item.prefix}`} className="text-[11px] font-bold uppercase tracking-wide pt-3 pb-0.5" style={{ color: "var(--yalola-primary)" }}>
                {item.prefix}
              </div>
            ) : (
              <div key={item.key} className="grid grid-cols-[190px_repeat(4,1fr)] gap-2 items-center" data-testid={`i18n-row-${item.key}`}>
                <div className="text-xs font-mono truncate" title={item.key} style={{ color: "var(--yalola-text-muted)" }}>{item.key}</div>
                {LANGS.map((l) => {
                  const changed = (rows[item.key]?.[l] ?? "") !== (orig[item.key]?.[l] ?? "");
                  return (
                    <Input
                      key={l}
                      value={rows[item.key]?.[l] ?? ""}
                      onChange={(e) => setVal(item.key, l, e.target.value)}
                      disabled={!isSuper}
                      className="h-8 text-sm"
                      style={changed ? { borderColor: "var(--yalola-primary)", background: "var(--yalola-primary-soft)" } : {}}
                      data-testid={`i18n-${item.key}-${l}`}
                    />
                  );
                })}
              </div>
            ))}
            {keys.length === 0 && <div className="text-sm text-center py-6" style={{ color: "var(--yalola-text-muted)" }}>Aucune traduction ne correspond à « {q} ».</div>}
          </div>
        </div>
      </div>
    </div>
  );
};
